'use client';

import '@/styles/news-styles.css';

import { faFacebook, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpFromBracket, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExtraSmallNewsCard from '@pickleballinc/features/src/hub-cards/ExtraSmallNewsCard/ExtraSmallNewsCard';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Modal, ModalContent } from '@pickleballinc/react-ui';
import { BettingAdvisory } from '@pickleballinc/ui/components/global/betting/BettingAdvisory';
import { BettingModal } from '@pickleballinc/ui/components/global/betting/BettingModal';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { renderContentWithAd } from '@pickleballinc/ui/components/global/utils/renderContentWithAd';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import moment from 'moment-timezone';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { Fragment, useEffect, useRef, useState } from 'react';

import { publicEnv } from '@/lib/env';
import EditButton from '@/modules/blog/article_components/EditButton';
import { FavoriteButton } from '@/modules/blog/article_components/FavoriteButton';
import ImagePreviewAndZoom from '@/modules/blog/article_components/ImagePreviewAndZoom';
import ShareDropdown from '@/modules/blog/article_components/ShareDropdown';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { Favorites } from '@/utils/actions/blog/areTheyFavorite';
import type { MainContentNewsInterface } from '@/utils/actions/homepage/getNewsBySlug';
import { RelatedNewsInterface } from '@/utils/actions/homepage/getRelatedNews';
import { BETTING_SITES_OPTIONS, PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';
import { getWithBaseUrl } from '@/utils/helpers/getWithBaseUrl';

declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

export interface ImgInfo {
  src: string;
  alt: string;
}

export interface MainContentProps {
  news: MainContentNewsInterface;
  relatedNews: RelatedNewsInterface[];
  favorite?: Favorites;
  totalFavoritesCount: string;
  ipAddress: string;
}

export const MainContent = ({
  news,
  relatedNews,
  favorite,
  totalFavoritesCount,
}: MainContentProps) => {
  const {
    title,
    authorFullName,
    content,
    facebookLink,
    imageAltText,
    imageCaption,
    imageSource,
    imageUrl,
    publishDateDisplayed,
    smsLink,
    twitterLink,
    newsCategory,
    slug,
    uuid,
    isBetting,
    bettingSite,
  } = news;
  const newsCategorySlug = newsCategory?.slug || 'news';
  const searchParams = useSearchParams();

  const articleURL = getWithBaseUrl(`/${newsCategorySlug}/${slug}`);

  const { session } = useSession();
  const loggedIn = session?.user;

  const { hasPermission } = useAuth();
  const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

  const shareDropdownRef = useRef<HTMLUListElement>(null);

  const [shareDropdownOpened, setShareDropdownOpened] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isFavorited, setIsFavorited] = useState(favorite?.moduleUuid !== undefined);
  const [favoritesCount, setFavoritesCount] = useState(
    totalFavoritesCount ? parseInt(totalFavoritesCount) : 0
  );

  const [bettingModalOpen, setBettingModalOpen] = useState(false);
  const [bettingUrl, setBettingUrl] = useState<string | null>(null);

  const [imgInfo, setImgInfo] = useState<ImgInfo>({ src: '', alt: '' });

  const { beforeAd, afterAd } = renderContentWithAd(content);

  const handleContainerClick = (event: MouseEvent) => {
    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    // Find closest <a> element in case the click is on child element (like <span> inside <a>)
    const link = target.closest('a');
    if (!(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute('href');
    if (!href) return;

    const containsBettingSite = BETTING_SITES_OPTIONS.some((option) =>
      href.toLowerCase().includes(option.label.toLowerCase())
    );

    if (containsBettingSite) {
      event.preventDefault();
      setBettingUrl(href);
      setBettingModalOpen(true);
    }
  };

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    const target = event.target as HTMLImageElement;
    const imgSrc = target.getAttribute('src') ?? '';
    const imgAlt = target.getAttribute('alt') ?? '';
    setImgInfo({ src: imgSrc, alt: imgAlt });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
        setShareDropdownOpened(false);
        setIsCopied(false);
      }
    };

    document.body.addEventListener('click', handleClickOutside);

    return () => {
      document.body.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const imageContainer = document.getElementById('news-content');

    if (imageContainer) {
      const images = imageContainer.querySelectorAll('img');

      images.forEach((imgElement) => {
        if (imgElement) {
          imgElement.addEventListener('click', handleClick);
        }
      });

      return () => {
        images.forEach((imgElement) => {
          if (imgElement) {
            imgElement.removeEventListener('click', handleClick);
          }
        });
      };
    }

    return () => {};
  }, []);

  useEffect(() => {
    if (window.instgrm) window.instgrm.Embeds.process();
  }, []);

  useEffect(() => {
    if (searchParams?.get('src') === 'pbgo') {
      const topNav = document.querySelector('#pb-topnav');
      const mainHeader = document.querySelector('#pb-mainheader');
      const footer = document.querySelector('#pb-footer');

      if (
        topNav instanceof HTMLElement &&
        mainHeader instanceof HTMLElement &&
        footer instanceof HTMLElement
      ) {
        topNav.style.display = 'none';
        mainHeader.style.display = 'none';
        footer.style.display = 'none';
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const isHTMLElement = (el: HTMLElement | null): el is HTMLElement => el !== null;

    const containers = [
      document.getElementById('news-content'),
      document.getElementById('before-ad-container'),
      document.getElementById('after-ad-container'),
    ].filter(isHTMLElement);

    containers.forEach((container) => container.addEventListener('click', handleContainerClick));

    return () => {
      containers.forEach((container) =>
        container.removeEventListener('click', handleContainerClick)
      );
    };
  }, [bettingSite, beforeAd, afterAd]);

  return (
    <Fragment>
      {imgInfo.src ? (
        <Modal
          open={imgInfo.src ? true : false}
          onOpenChange={() => setImgInfo({ src: '', alt: '' })}
        >
          <ModalContent className='flex !h-full flex-col items-center justify-center !border-none !bg-transparent !p-4 !outline-none sm:!p-8'>
            <ImagePreviewAndZoom
              imgInfo={imgInfo}
              resetImgURL={() => setImgInfo({ src: '', alt: '' })}
            />
          </ModalContent>
        </Modal>
      ) : null}
      <div className='flex size-full flex-col' itemScope itemType='https://schema.org/NewsArticle'>
        <meta itemProp='url' content={articleURL} />
        <div
          className='flex flex-1 flex-col justify-center overflow-auto pb-4 pt-5 max-w-[704px]'
          id='news-content'
        >
          <div className='w-full'>
            {isBetting ? (
              <div className='px-4 pb-3'>
                <BettingAdvisory pickleballHref={`${publicEnv.NEXT_PUBLIC_HOME_ROOT_URL}`} />
              </div>
            ) : null}
            <figure className='mb-4 w-full overflow-hidden rounded-lg sm:min-h-[251px] px-4'>
              <img
                src={imageUrl || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 269, 269)}`}
                alt={imageAltText || title}
                data-default-src={
                  imageUrl || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 269, 269)}`
                }
                className='block size-full max-h-[269px] bg-cover object-cover sm:min-h-[269px] rounded-t-lg'
                itemProp='thumbnail'
              />
              {(imageCaption && imageCaption.length > 0) ||
              (imageSource && imageSource.length > 0) ? (
                <figcaption className='mt-2.5 block px-0.5 text-xs leading-4 text-gray-500'>
                  {imageCaption ? <>{imageCaption}&nbsp;</> : null}
                  {imageSource ? (
                    <cite className='not-italic text-[#a5a6a7]'>{imageSource}</cite>
                  ) : null}
                </figcaption>
              ) : null}
            </figure>
            <h2
              className='relative z-30 mb-6 text-3xl font-bold leading-[1.2] text-gray-900 outline-none sm:text-4xl px-4'
              itemProp='headline'
            >
              {title}
            </h2>
            <div className='mb-6 flex items-center gap-2 border-b border-gray-200 pb-3 px-4'>
              <div className='flex gap-1'>
                {/* TODO: Add author image when it arrives in payload
                 <Avatar size="sm" /> 
                */}
                <div className='flex flex-col justify-center gap-1'>
                  <div
                    className='relative text-xs font-medium leading-none text-gray-900'
                    itemScope
                    itemProp='author'
                    itemType='https://schema.org/Person'
                  >
                    <span itemProp='name'>{authorFullName}</span>
                  </div>
                  {publishDateDisplayed ? (
                    <div
                      className='relative text-xs leading-none text-[#344054]'
                      itemProp='datePublished'
                      content={moment(publishDateDisplayed).format('YYYY–MM-DD HH:mm:ss')}
                    >
                      {formatDateTime(publishDateDisplayed, {
                        timezone: 'America/New_York',
                        timezoneAbbreviation: 'ET',
                        type: 'datetime',
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className='flex items-center gap-3 sm:justify-center'>
                {facebookLink ? (
                  <a
                    href={facebookLink}
                    rel='noopener noreferrer'
                    target='_blank'
                    aria-label='Link to Facebook (opens in a new tab)'
                  >
                    <FontAwesomeIcon className='text-gray-900' icon={faFacebook} size='xl' />
                  </a>
                ) : null}
                {twitterLink ? (
                  <a
                    href={twitterLink}
                    rel='noopener noreferrer'
                    target='_blank'
                    aria-label='Link to X (opens in a new tab)'
                  >
                    <FontAwesomeIcon className='text-gray-900' icon={faXTwitter} size='xl' />
                  </a>
                ) : null}
                {smsLink ? (
                  <a
                    href={smsLink}
                    rel='noopener noreferrer'
                    target='_blank'
                    aria-label='Link to SMS (opens in a new tab)'
                  >
                    <FontAwesomeIcon className='text-gray-900' icon={faEnvelope} size='xl' />
                  </a>
                ) : null}
              </div>
            </div>
            <div
              className='like-and-share relative mb-12 flex justify-between flex-wrap items-center px-4'
              data-behavior='content_reactions'
              data-contenttitle={title}
            >
              <div className='flex flex-wrap mt-1'>
                <div className='flex flex-row mr-3 sm:mr-0 sm:-ml-3 sm:pr-0'>
                  <button
                    className='flex cursor-pointer flex-row items-center gap-1.5 rounded-[100px] sm:px-3 sm:py-1.5 sm:hover:bg-gray-100'
                    onClick={() => setShareDropdownOpened(true)}
                    style={{
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      userSelect: 'none',
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowUpFromBracket} size='lg' />
                    <span className='text-sm font-semibold not-italic text-gray-900 sm:leading-5'>
                      Share
                    </span>
                  </button>
                </div>
                {shareDropdownOpened ? (
                  <ShareDropdown
                    shareDropdownRef={shareDropdownRef}
                    isCopied={isCopied}
                    setIsCopied={setIsCopied}
                    articleURL={articleURL}
                    title={title}
                  />
                ) : null}
                {isSuperAdmin ? <EditButton articleSlug={slug} /> : null}
                {loggedIn ? (
                  <FavoriteButton
                    moduleUuid={uuid}
                    isFavorited={isFavorited}
                    setIsFavorited={setIsFavorited}
                    setFavoritesCount={setFavoritesCount}
                    favoritesCount={favoritesCount}
                  />
                ) : null}
              </div>
              {favoritesCount > 0 && (
                <div className='text-xs text-gray-500 mt-1'>
                  <span className='mr-2'>
                    <FontAwesomeIcon icon={faHeart} size='lg' className='text-error-500 text-xl' />
                  </span>
                  {isFavorited
                    ? favoritesCount === 1
                      ? 'You'
                      : `You and ${favoritesCount - 1} ${favoritesCount === 2 ? 'other' : 'others'}`
                    : favoritesCount}
                </div>
              )}
            </div>
            <div className='news-preview relative' itemProp='text' id='before-ad-container'>
              {beforeAd
                ? (() => {
                    return (
                      <div className='news-preview relative' itemProp='text'>
                        <div className='px-4'>{beforeAd}</div>
                      </div>
                    );
                  })()
                : null}
            </div>
            {/* <RenderVideoAd
              zoneId={ZONE_IDS_PB.FLOATING_VIDEO}
              pageId={100002}
              place={4}
              ipAddress={ipAddress}
              isProd={IS_PRODUCTION}
              containerClassname='mx-auto'
            /> */}
            <div className='news-preview relative' itemProp='text' id='after-ad-container'>
              {afterAd
                ? (() => {
                    return (
                      <div className='news-preview relative' itemProp='text'>
                        <div className='px-4'>{afterAd}</div>
                      </div>
                    );
                  })()
                : null}
            </div>
          </div>
        </div>

        {bettingModalOpen && bettingUrl ? (
          <BettingModal
            open={bettingModalOpen}
            onClose={() => setBettingModalOpen(false)}
            onConfirm={() => {
              setBettingModalOpen(false);
              if (bettingUrl) window.open(bettingUrl, '_blank');
            }}
            bettingSite={bettingSite || 'the betting site'}
            pickleballImage={`${getCDNURL(`${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PB-colored.svg`, 68, 68)}`}
          />
        ) : null}

        {relatedNews.length > 0 && (
          <div className='mt-6 px-4 pb-4'>
            <h2 className='mb-6 text-3xl font-bold leading-[1.2] text-gray-900 outline-none sm:text-4xl'>
              Related articles
            </h2>
            <div className='grid w-full max-w-2xl grid-cols-1 gap-4 pb-2 sm:grid-cols-2'>
              {relatedNews.map((article) => (
                <CustomLink
                  key={`related-article-${article.uuid}`}
                  className='w-full hover:opacity-80'
                  href={article.externalUrl || `/${article.categorySlug}/${article.slug}`}
                  rel='noopener noreferrer'
                  target={article.externalUrl ? '_blank' : '_self'}
                >
                  <ExtraSmallNewsCard
                    oneLineForDescription
                    image={article.imageUrl || article.smallNewsCardImgUrl || ''}
                    imageAltText={article.imageAltText}
                    title={article.title}
                    description={article.description || ''}
                    publishDateDisplayed={article.publishDateDisplayed}
                    author={article.authorFullName || ''}
                  />
                </CustomLink>
              ))}
            </div>
          </div>
        )}
      </div>
      <Script
        async
        defer
        strategy='lazyOnload'
        type='text/javascript'
        src='https://www.instagram.com/embed.js'
      />
      <Script
        async
        defer
        strategy='lazyOnload'
        type='text/javascript'
        src='https://platform.twitter.com/widgets.js'
        charSet='utf-8'
      />
    </Fragment>
  );
};
